/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import { JobCardBaseProps, NavigateHandler } from '../../index';
import {
  CardLayout as Card,
  Header,
  Body,
  Footer,
  getHeaderProps,
  getFooterProps,
  type GetPropsParam,
} from '../Layout';
import { InfoList } from '../InfoList';
import { Media } from '../Media';
import { AppealBC } from '../Appeal';
import styles from './index.module.scss';

/**
 * JobCardBCProps インターフェース
 * JobCardBasePropsを拡張したプロパティ
 */
interface JobCardBCProps extends JobCardBaseProps {}

/**
 * JobCardBC コンポーネント
 * @param {JobCardBCProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} JobCardBCのJSX要素
 */
export const JobCardBC = ({
  job,
  destinationURL,
  imagePriority,
  bookmarkUrlQuery,
  similarKeywordArea,
  ...handlers
}: JobCardBCProps) => {
  const {
    corporateName,
    occupationName,
    isApplicationWelcome,
    isApplied,
    isBookmarkSaved,
    jobContentOutline,
    targetMemberOutline,
    jobState,
    stations,
    salary,
    salaryExamples,
    businessContent,
    companyOverviewHeading,
    companyOverviewSubHeading,
    companyOverviewText,
    imagePath,
    url = '',
  } = job;

  const {
    onNavigateTitle,
    onNavigateFooterButton,
    onApplyDirect,
    onApplyAgent,
    onNavigateImage,
    onBookmark,
    onNavigateToBookmarkList,
  } = handlers;

  /**
   * ヘッダーとフッターのプロパティを取得
   */
  const [headerProps, footerProps] = useMemo(() => {
    const arg: GetPropsParam = { job };
    return [getHeaderProps(arg), getFooterProps(arg)];
  }, [job]);

  /**
   * 求人詳細ページへのナビゲートハンドラーを生成
   * @param {NavigateHandler} handler - デフォルトのハンドラー
   * @returns {NavigateHandler} ナビゲートハンドラー
   */
  const handleNavigateToJobDetail =
    (handler?: NavigateHandler): NavigateHandler =>
    (event) => {
      event.currentTarget.href = destinationURL;
      if (handler) handler(event);
    };

  return (
    <Card
      isWelcome={isApplicationWelcome}
      isApplied={isApplied}
      isBookmarked={isBookmarkSaved}
    >
      <Header
        {...headerProps}
        onNavigateTitle={handleNavigateToJobDetail(onNavigateTitle)}
        onApplyDirect={onApplyDirect}
        onApplyAgent={onApplyAgent}
        similarKeywordArea={similarKeywordArea}
      />
      <Body>
        <div className={styles['jobCardBC-body']}>
          <InfoList
            detail={jobContentOutline}
            target={targetMemberOutline}
            location={jobState}
            station={stations}
            salary={{
              salary,
              example: salaryExamples,
            }}
            business={businessContent}
          />
          <div className={styles['jobCardBC-body__right']}>
            <div className={styles['jobCardBC-body__appeal']}>
              <AppealBC
                title={companyOverviewHeading}
                subTitle={companyOverviewSubHeading}
                body={companyOverviewText}
              />
            </div>
            <Media
              cardType="BC"
              corporateName={corporateName}
              occupationName={occupationName}
              imagePath={imagePath}
              imgHref={url}
              onNavigate={handleNavigateToJobDetail(onNavigateImage)}
              imagePriority={imagePriority}
            />
          </div>
        </div>
      </Body>
      <Footer
        {...footerProps}
        bookmarkUrlQuery={bookmarkUrlQuery}
        onNavigateFooterButton={handleNavigateToJobDetail(
          onNavigateFooterButton
        )}
        onBookmark={onBookmark}
        onNavigateToBookmarkList={onNavigateToBookmarkList}
      />
    </Card>
  );
};
